import moment from 'moment';
import Handlebars from 'handlebars';

/**
 * Distinguish block usage from subexpression usage so both stay supported:
 *
 * - Block (backwards compatible): `{{#greaterThan a b}} … {{else}} … {{/greaterThan}}`
 *   Handlebars passes `options` with callable `fn` and `inverse`.
 *
 * - Subexpression: `{{#if (gt a b)}}` — the inner `(gt a b)` is evaluated as a boolean;
 *   `options` has no `fn` / `inverse` (verified for handlebars@4.x).
 *
 * Same pattern applies to equals, and, or, etc.
 */
function isBlockHelperMode(options) {
  return (
    options &&
    typeof options.fn === 'function' &&
    typeof options.inverse === 'function'
  );
}

const registerHelpers = Handlebars => {
  Handlebars.registerHelper('and', function(arg1, arg2, options) {
    if (isBlockHelperMode(options)) {
      return arg1 && arg2 ? options.fn(this) : options.inverse(this);
    }
    return arg1 && arg2;
  });
  Handlebars.registerHelper('or', function(arg1, arg2, options) {
    if (isBlockHelperMode(options)) {
      return arg1 || arg2 ? options.fn(this) : options.inverse(this);
    }
    return arg1 || arg2;
  });
  Handlebars.registerHelper('equals', function(arg1, arg2, options) {
    const ok = arg1 == arg2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('eq', function(a, b, options) {
    const ok = a == b;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('notEquals', function(a, b, options) {
    const ok = a != b;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('lessThan', function(v1, v2, options) {
    const ok = v1 < v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('lt', function(v1, v2, options) {
    const ok = v1 < v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('greaterThan', function(v1, v2, options) {
    const ok = v1 > v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('gt', function(v1, v2, options) {
    const ok = v1 > v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('gte', function(v1, v2, options) {
    const ok = v1 >= v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('lte', function(v1, v2, options) {
    const ok = v1 <= v2;
    if (isBlockHelperMode(options)) {
      return ok ? options.fn(this) : options.inverse(this);
    }
    return ok;
  });
  Handlebars.registerHelper('length', function(v1, options) {
    options.name = 'no-hash';
    return v1.length;
  });
  // Handlebars.registerHelper('add', function(v1, v2, options) {
  //   // not a sendgrid helper - used to add 1 to an index within an each loop
  //   return parseInt(v1) + parseInt(v2);
  // });
  Handlebars.registerHelper('formatDate', function(
    timeStamp,
    dateFormat,
    timezoneOffset,
    options
  ) {
    if (timeStamp && typeof timeStamp === 'string') {
      if (typeof timezoneOffset === 'string') {
        return moment(timeStamp)
          .utcOffset(timezoneOffset)
          .format(dateFormat);
      }

      return moment(timeStamp).format(dateFormat);
    }
    return null;
  });
  // Handlebars.registerHelper('today', function() {
  //   return moment().format('YYYY-MM-DD');
  // });
  Handlebars.registerHelper('insert', function(value, defaultValue) {
    if (value) {
      return value;
    } else {
      const containsDefaultString = defaultValue.indexOf('default=');
      const split = defaultValue.split('default=');
      return containsDefaultString !== -1 ? split[1] : '';
    }
  });
  /** Safe for query strings, e.g. {{urlEncode appToken}} */
  Handlebars.registerHelper('urlEncode', function(value) {
    if (value == null || value === '') {
      return '';
    }
    return encodeURIComponent(String(value));
  });
  Handlebars.registerHelper('encodeURIComponent', function(value) {
    if (value == null || value === '') {
      return '';
    }
    return encodeURIComponent(String(value));
  });
  Handlebars.registerHelper('unless', function(value, options) {
    if (!value) {
      return options.fn(this);
    }
    return null;
  });
};
registerHelpers(Handlebars);
export default Handlebars;
